import {
  useCreateLocationNote,
  useLocationNotes,
} from '@hakimo-ui/hakimo/data-access';
import { LocationNote } from '@hakimo-ui/hakimo/types';
import { LoadingIndicator } from '@hakimo-ui/hakimo/ui-elements';
import { toast } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

interface Props {
  locationId: string;
}

export function LocationNotesContent({ locationId }: Props) {
  const [newNoteContent, setNewNoteContent] = useState('');

  const { data: notesData, isLoading } = useLocationNotes(locationId);
  const createNoteMutation = useCreateLocationNote(locationId);

  const notes = notesData?.notes || [];

  const handleSubmit = async () => {
    if (!newNoteContent.trim()) return;

    try {
      await createNoteMutation.mutateAsync({ content: newNoteContent.trim() });
      setNewNoteContent('');
    } catch (error) {
      toast('Failed to create note', { type: 'error' });
    }
  };

  return (
    <div>
      {isLoading && (
        <div className="flex justify-center py-4">
          <LoadingIndicator text="Loading notes..." />
        </div>
      )}
      {!isLoading && notes.length > 0 && (
        <div className="max-h-96 overflow-y-auto overflow-x-hidden">
          <ul className="list-disc space-y-2">
            {notes.map((note: LocationNote) => (
              <li key={note.id} className="ml-4 list-item">
                <div className="break-words">{note.content}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 pt-3">
        <div className="flex gap-2">
          <div className="flex-1">
            <textarea
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Type your Notes..."
              className="dark:bg-ondark-bg-2 dark:border-ondark-line-2 border-onlight-line-2 max-h-10 w-full resize-none rounded-md border p-2 text-sm"
              disabled={createNoteMutation.isLoading}
            />
          </div>
          <Button
            variant="icon"
            onClick={handleSubmit}
            disabled={!newNoteContent.trim() || createNoteMutation.isLoading}
            className="self-center"
            loading={createNoteMutation.isLoading}
          >
            {!createNoteMutation.isLoading && (
              <PaperAirplaneIcon className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
